import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Row, Col, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import dataServices from "../../apiServices/data.services";
import { useAlert } from "react-alert";
import Loader from "../../components/Loader/Loader";
import { Can } from "../../context/AuthProvider";
import { handleValidationError } from "../../components/CommonFunctions";

const EditPlan = () => {
  const { id } = useParams();
  const alert = useAlert();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dataServices
      .getPlanDetails(id)
      .then((res) => {
        if (res.status === 200) {
          setPlan(res.data);
        }
      })
      .catch((err) => {
        alert.error(handleValidationError(err));
      })
      .finally(() => setLoading(false));
  }, [id, alert]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: plan?.name || "",
      price: plan?.price || "",
      billing_cycle: plan?.billing_cycle || "monthly",
      description: plan?.description || "",
      status: plan?.status || "active",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Plan name is required"),
      price: Yup.number().typeError("Price must be a number").required("Price is required"),
      billing_cycle: Yup.string().required("Billing cycle is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      dataServices
        .editPlan(id, values)
        .then((res) => {
          if (res.status === 200) {
            alert.success("Plan updated successfully");
            navigate("/plans");
          }
        })
        .catch((err) => {
          alert.error(handleValidationError(err));
        })
        .finally(() => setLoading(false));
    },
  });

  if (!plan) {
    return <Loader />;
  }

  return (
    <div className="content-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-2 pb-1 fw-bold text-black">Edit Plan</h6>
      </div>
      <Can I="edit" this="Plan">
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="name">Plan Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  id="name"
                  name="name"
                  type="text"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={
                    formik.touched.name && formik.errors.name ? "form-control is-invalid" : "form-control"
                  }
                />
                {formik.touched.name && formik.errors.name ? (
                  <div className="invalid-feedback">{formik.errors.name}</div>
                ) : null}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="price">Price <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  id="price"
                  name="price"
                  type="text"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={
                    formik.touched.price && formik.errors.price ? "form-control is-invalid" : "form-control"
                  }
                />
                {formik.touched.price && formik.errors.price ? (
                  <div className="invalid-feedback">{formik.errors.price}</div>
                ) : null}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="billing_cycle">Billing Cycle <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="select"
                  id="billing_cycle"
                  name="billing_cycle"
                  value={formik.values.billing_cycle}
                  onChange={formik.handleChange}
                  className="form-control"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="status">Status <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="select"
                  id="status"
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  className="form-control"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col sm={12}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="description">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  id="description"
                  name="description"
                  rows={3}
                  className="form-control"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button type="button" className="btn btn-secondary w-100" onClick={() => navigate("/plans")}>
                Cancel
              </Button>
            </Col>
            <Col className="footer mt-auto">
              <Button type="submit" disabled={loading} className="btn btn-primary w-100">
                Save Changes
              </Button>
            </Col>
          </Row>
        </Form>
      </Can>
    </div>
  );
};

export default EditPlan;
